/**
 * 会议管控（主持人）：一组作用于全体成员的开关。
 *
 * 对齐腾讯会议的「会议管控」面板。几十人的教室里逐个点成员改权限没法用，
 * 这些必须是全局开关。
 *
 * 主持人与联席主持人不受这些开关影响 —— 服务端也是这么做的
 * （见 room/control.go）。把自己一起限制掉就没人能改回来了。
 */

import { hasPerm, Perm, RoomFlag } from '../lib/protocol'
import { useRoomStore } from '../stores/roomStore'

/** 一个开关：allow 为真表示「允许成员 X」。 */
interface Switch {
  key: string
  label: string
  hint: string
  /** 对应的权限位；用房间标志位的那几项走 flag 字段 */
  perm?: number
  flag?: number
}

const SWITCHES: Switch[] = [
  { key: 'speak', label: '允许成员发言', hint: '关掉相当于全体静音，学员说不了话', perm: Perm.Speak },
  { key: 'video', label: '允许成员开摄像头', hint: '关掉后学员不能开视频', perm: Perm.Video },
  { key: 'write', label: '允许成员在交流区发言', hint: '关掉后学员发不了消息', perm: Perm.Write },
  { key: 'share', label: '允许成员共享屏幕', hint: '关掉后只有主持人能共享', perm: Perm.AppShare },
  { key: 'wb', label: '允许成员使用白板', hint: '关掉后学员不能在白板上画', perm: Perm.Whiteboard },
  { key: 'free', label: '自由发言', hint: '开启则人人可自行开麦；关闭则要申请、由你逐个给麦', flag: RoomFlag.StateFreeSpeak },
  {
    key: 'muteJoin',
    label: '成员入会时静音',
    hint: '之后进来的人默认不开麦，自己可以打开。大班课陆续进场时省掉一遍「全体静音」',
    flag: RoomFlag.StateMuteOnJoin,
  },
  { key: 'wait', label: '开启等候室', hint: '学员进教室要先经你允许', flag: RoomFlag.StateWaitingRoom },
  { key: 'lock', label: '锁定教室', hint: '锁定后新人进不来', flag: RoomFlag.StateLocked },
]

export function MeetingControl({ onClose }: { onClose: () => void }) {
  const room = useRoomStore((s) => s.room)
  const members = useRoomStore((s) => s.members)
  const selfId = useRoomStore((s) => s.selfId)
  const setAllProp = useRoomStore((s) => s.setAllUsersProp)
  const unmuteAll = useRoomStore((s) => s.unmuteAll)
  const setRoomFlags = useRoomStore((s) => s.setRoomFlags)
  const me = members.get(selfId)

  // 「成员当前是否被允许」按普通成员的实际权限判断，而不是记一份本地状态 ——
  // 别的主持人也可能改，本地状态会和服务端不一致。
  const others = [...members.values()].filter((m) => m.id !== selfId && !hasPerm(m.prop, Perm.Op))
  const allowed = (perm: number) => others.length === 0 || others.every((m) => hasPerm(m.prop, perm))
  const flagOn = (flag: number) => !!room && (room.flags & flag) !== 0

  const toggle = (sw: Switch) => {
    if (sw.flag !== undefined) {
      setRoomFlags(sw.flag, flagOn(sw.flag) ? 'remove' : 'add')
      return
    }
    if (sw.perm === undefined) return
    setAllProp(sw.perm, allowed(sw.perm) ? 'remove' : 'add')
  }

  return (
    <div className="dlg-mask" onClick={onClose}>
      <div className="dlg narrow mc-dlg" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="会议管控">
        <h3>会议管控</h3>
        <p className="mc-sum">
          这些开关作用于全体学员（{others.length} 人）。你和联席主持人不受影响。
        </p>
        <ul className="mc-list">
          {SWITCHES.map((sw) => {
            const on = sw.flag !== undefined ? flagOn(sw.flag) : allowed(sw.perm!)
            // 不能授予自己都没有的权限，服务端会拒 —— 这里先禁掉，别让人白点。
            const disabled = sw.perm !== undefined && !!me && !hasPerm(me.prop, sw.perm)
            return (
              <li key={sw.key}>
                <label className="mc-row">
                  <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggle(sw)} />
                  <span className="mc-label">
                    {sw.label}
                    <em className="mc-hint">{sw.hint}</em>
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
        <div className="dlg-foot">
          <button onClick={unmuteAll}>解除全体静音</button>
          <button className="primary" onClick={onClose}>完成</button>
        </div>
      </div>
    </div>
  )
}
